import { values, keys, union, mapValues } from 'lodash';
import { utils } from './';
import JSON5 from 'json5';

export const AVERAGECPUUSAGE = 'AVERAGECPUUSAGE';
export const AVERAGERAMUSAGE = 'AVERAGERAMUSAGE';
export const CCF_ARTIFACTS_DIFFRESULT = 'CCF_ARTIFACTS_DIFFRESULT';
export const CCF_ERROR_CODE_DIFFRESULT = 'CCF_ERROR_CODE_DIFFRESULT';
export const CCF_SOURCES_DIFFRESULT = 'CCF_SOURCES_DIFFRESULT';
export const CCF_UNHANDLEDARTIFACTCOUNT = 'CCF_UNHANDLEDARTIFACTCOUNT';
export const HIGHESTCPUUSAGEPEAK = 'HIGHESTCPUUSAGEPEAK';
export const HIGHESTRAMUSAGEUSAGEPEAK = 'HIGHESTRAMUSAGEUSAGEPEAK';
export const NCF_ARTIFACTS_DIFFRESULT = 'NCF_ARTIFACTS_DIFFRESULT';
export const NCF_ERROR_CODE_DIFFRESULT = 'NCF_ERROR_CODE_DIFFRESULT';
export const NCF_MCAT_DIFFRESULT = 'NCF_MEDIACATEGORY_DIFFRESULT';
export const NCF_SOURCES_DIFFRESULT = 'NCF_SOURCES_DIFFRESULT';
export const NCF_UNHANDLEDARTIFACTCOUNT = 'NCF_UNHANDLEDARTIFACTCOUNT';
export const RUN_CASE_METRICS_DIFF_ERROR_CODE_SUFFIX = '_DIFF_ERROR_CODE';
export const RUN_CASE_METRICS_DIFF_OUTPUT_CODE_SUFFIX = '_DIFF_OUTPUT_CODE';
export const RUN_CASE_METRICS_DIFF_RESULT_SUFFIX = '_DIFFRESULT';
export const RUN_CASE_METRICS_DIFF_RETURN_CODE_SUFFIX = '_DIFF_RETURN_CODE';
export const RUN_CASE_METRICS_DIFF_STATUS_CODE_SUFFIX = '_DIFF_STATUS_CODE';

const parseJsonStringMetric = (metric) =>
  JSON5.parse(metric.slice(1, metric.length - 1));
export const metricsOrder = [
  'EXPORTSTATUS',
  'EXPORTDURATION',
  'EXPORTSIZE',
  'VALIDITY',
];
export const getMetricBadge = (metrics, metricsLoaded, ccfNcfOrder) => {
  const getValidMetrics = (type) => metrics.filter(m =>
    m[type] &&
    (isNaN(m[type].durationMetrics.current) ||
      m[type].durationMetrics.current > 30)
  ).map(m => m[type].durationMetrics.percent);
  const concerning = getValidMetrics(
    ccfNcfOrder.main
  ).concat(ccfNcfOrder.other ? getValidMetrics(
    ccfNcfOrder.other
  ) : []);
  const maxDurationPercent = concerning.length > 0 ?
    Math.max(...concerning) :
    0;

  if (maxDurationPercent === -Infinity ||
      !metricsLoaded ||
      isNaN(maxDurationPercent)) {
    return {
      text: 'None',
      style: 'warning'
    };
  } else if (maxDurationPercent > 5) {
    return {
      text: `Large Duration Increase: ${maxDurationPercent}%`,
      style: 'danger'
    };
  } else if (maxDurationPercent > 0) {
    return {
      text: `Slight Duration Increase: ${maxDurationPercent}%`,
      style: 'warning'
    };
  }

  return {
    text: 'Good Performance',
    style: 'primary'
  };
};

export const getHitCountStyle = (metrics, ccfNcfOrder) => {
  const findBadDiff = (type) => metrics
    .find(m => m[type].durationMetrics.current > 30 &&
               Math.abs(m[type].hitCountMetrics.diff) > 0);
  let badDiff = findBadDiff(ccfNcfOrder.main);

  if (!badDiff && ccfNcfOrder.other) {
    badDiff = findBadDiff(ccfNcfOrder.other);
  }

  return badDiff ? 'danger' : 'primary';
};

export const getDiffResult = (resultType, metrics, isCcf) =>
  metrics && metrics[`${isCcf ? 'C' : 'N'}CF_${resultType}_DIFFRESULT`];
export const getSearchDuration = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_SEARCHDURATION`];
export const getHitCount = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_ARTIFACTHITCOUNT`];
export const getCaseSize = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_CASESIZE`];
export const getDetailedEngineTiming = (metrics, isCcf) =>
  metrics && metrics[`${isCcf ? 'C' : 'N'}CF_ENGINE_TIMING`];
export const getIndexingDuration = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_INDEXINGDURATION`];
export const getDecryptionDuration = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_DECRYPTIONDURATION`];
export const getArtifactSummary = (metrics, isCcf) =>
  metrics && metrics[`${isCcf ? 'C' : 'N'}CF_ARTIFACT_SUMMARY`];
export const getArtifactWorkerFarms = (metrics, isCcf) =>
  metrics && metrics[`${isCcf ? 'C' : 'N'}CF_ARTIFACT_WORKER_FARMS`];
export const getPerformanceDuration = (metrics, caseAction) =>
  metrics && +metrics[caseAction];
export const getSourceCount = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_SOURCECOUNT`];
export const getSourceAttributeCount = (metrics, isCcf) =>
  metrics && +metrics[`${isCcf ? 'C' : 'N'}CF_SOURCEATTRIBUTECOUNT`];
export const getExportDuration = (metrics) =>
  metrics && +metrics['EXPORTDURATION'];
export const getCaseAmendingDuration = (metrics) =>
  metrics && +metrics['JOB_DURATION'];
export const getJobBenchmarkDuration = (metrics) =>
  metrics && +metrics['MEAN'];
export const getMcatDiffResult = (metrics, isCcf) =>
  metrics && (!isCcf) && metrics['NCF_MEDIACATEGORY_DIFFRESULT'];

export const getArtifactTimings = (metrics, isCcf) => {
  if (!metrics) { return undefined; }
  const timingKeys = keys(metrics)
    .filter(key => key.startsWith(`${isCcf ? 'C' : 'N'}CF_ARTIFACTTIMINGS`));
  return timingKeys.reduce((result, key) => {
    result[key] = metrics[key];
    return result;
  }, {});
};

export const getDetailedEngineTimingObject = (metrics, isCcf) => {
  const engine = getDetailedEngineTiming(metrics, isCcf);
  if (!engine) return {};
  const engineObj = parseJsonStringMetric(engine);
  return values(engineObj).reduce((acc, map) => {
    const result = { ...acc };
    Object.keys(map).forEach(key => {
      const resultVal = result[key] ? result[key] : 0;
      result[key] = resultVal + map[key];
    });
    return result;
  }, {});
};

export const getArtifactTimingObject = (metrics, isCcf) => {
  const summary = getArtifactSummary(metrics, isCcf);
  const timings = getArtifactTimings(metrics, isCcf);
  const farms = getArtifactWorkerFarms(metrics, isCcf);

  return {
    'summary': summary ? parseJsonStringMetric(summary) : {},
    'timings': timings ? mapValues(timings, t => parseJsonStringMetric(t)) : {},
    'workerFarms': farms ? parseJsonStringMetric(farms) : {}
  };
};

export const getArtifactTimingHunters = (huntersByGroupByCarve) => {
  return keys(huntersByGroupByCarve).reduce((result, carveType) => {
    keys(huntersByGroupByCarve[carveType]).forEach(group => {
      const hunters = huntersByGroupByCarve[carveType][group];
      if (hunters) {
        result = { ...result, ...hunters };
      }
    });
    return result;
  }, {});
};

export const getArtifactHitCounts = (metrics, isCcf) => {
  if (!metrics) {
    return {};
  }

  const prefix = (isCcf ? 'CCF' : 'NCF');

  if (metrics['ARTIFACTHITCOUNT'] && metrics['ARTIFACTHITCOUNT'][prefix]) {
    return mapValues(metrics['ARTIFACTHITCOUNT'][prefix], m => +m);
  }

  return {};
};

export const getResourceUsages = (metrics, isCcf) => {
  if (!metrics) {
    return {};
  }
  const prefix = (isCcf ? 'CCF' : 'NCF');
  if (metrics['RESOURCEUSAGE'] && metrics['RESOURCEUSAGE'][prefix]) {
    return mapValues(metrics['RESOURCEUSAGE'][prefix], m => +m);
  }

  return {};
};

export const getSourceCounts = (metrics, ccf) => {
  const prefix = (ccf ? 'CCF' : 'NCF') + '_SOURCECOUNT';
  return utils.sumArrayByKeyName(metrics, prefix);
};

export const getSourceAttributeCounts = (metrics, ccf) => {
  const prefix = (ccf ? 'CCF' : 'NCF') + '_SOURCEATTRIBUTECOUNT';
  return utils.sumArrayByKeyName(metrics, prefix);
};

export const artifactCarvingTypes = (isCcf) => {
  const first = isCcf ? 'C' : 'N';
  return [
    `${first}CF_ARTIFACTTIMINGS:REGEXCARVING`,
    `${first}CF_ARTIFACTTIMINGS:MANUALCARVING`,
    `${first}CF_ARTIFACTTIMINGS:SIMPLECARVING`,
    `${first}CF_ARTIFACTTIMINGS:NONCARVING`
  ];
};

export const metricsSorterTypes = {
  SORT_IMAGE: 0,
  SORT_DURATION_CURRENT: 1,
  SORT_DURATION_BASELINE: 2,
  SORT_DURATION_PERCENT: 3,
  SORT_DURATION_DIFF: 4,
  SORT_HITCOUNT_CURRENT: 5,
  SORT_HITCOUNT_BASELINE: 6,
  SORT_HITCOUNT_DIFF: 7,
  SORT_SOURCECOUNT_CURRENT: 8,
  SORT_SOURCECOUNT_BASELINE: 9,
  SORT_SOURCECOUNT_DIFF: 10,
  SORT_SOURCEATTRIBUTECOUNT_CURRENT: 11,
  SORT_SOURCEATTRIBUTECOUNT_BASELINE: 12,
  SORT_SOURCEATTRIBUTECOUNT_DIFF: 13,
  SORT_HITCOUNT_DIFFRESULT: 14,
  SORT_SOURCECOUNT_DIFFRESULT: 15,
  SORT_HITCOUNT_MCAT:16
};

export const getSummedWorkerThreadDequeueTimes = (metrics, isCcf) => {
  const workerFarms = getArtifactWorkerFarms(metrics, isCcf);
  if (!workerFarms) return {};
  const workerFarmObj = parseJsonStringMetric(workerFarms);
  const timing = values(workerFarmObj).reduce((acc, map) => {
    Object.values(map.WorkerThreads).forEach(thread => {
      acc += Number.isInteger(thread.DequeueTime && thread.DequeueTime.ElapsedMs) ?
        thread.DequeueTime.ElapsedMs : 0;
    });
    return acc;
  }, 0);
  return { 'TotalWorkerThreadDequeueTime': timing };
};

const sortBySelector = (selector, asc) => {
  return (a, b) => {
    const orderConst = asc ? 1 : -1;
    let aVal = parseInt(selector(a));
    let bVal = parseInt(selector(b));

    aVal = isNaN(aVal) ?
      (aVal === '-Infinity' ? -Infinity : Infinity) :
      aVal;
    bVal = isNaN(bVal) ?
      (bVal === '-Infinity' ? -Infinity : Infinity) :
      bVal;

    if (aVal === bVal) {
      return orderConst * (a.hash - b.hash);
    }

    return orderConst * (aVal - bVal);
  };
};

const sortByImage = (asc) => {
  return (a, b) => {
    const orderConst = asc ? 1 : -1;
    return orderConst *
      (a.image + a.createdDate).localeCompare((b.image + b.createdDate));
  };
};

export const sortMetrics = (metrics, sorter) => {
  let sortFunc;
  const type = sorter.isCcf ? 'ccf' : 'ncf';

  switch (sorter.id) {
    case metricsSorterTypes.SORT_DURATION_CURRENT:
      sortFunc = sortBySelector(m => m[type].durationMetrics.current, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_DURATION_BASELINE:
      sortFunc = sortBySelector(m => m[type].durationMetrics.baseline, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_DURATION_PERCENT:
      sortFunc = sortBySelector(m => m[type].durationMetrics.percent, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_DURATION_DIFF:
      sortFunc = sortBySelector(m => m[type].durationMetrics.diff, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_HITCOUNT_CURRENT:
      sortFunc = sortBySelector(m => m[type].hitCountMetrics.current, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_HITCOUNT_BASELINE:
      sortFunc = sortBySelector(m => m[type].hitCountMetrics.baseline, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_HITCOUNT_DIFF:
      sortFunc = sortBySelector(m => m[type].hitCountMetrics.diff, sorter.ascending);
      break;

    case metricsSorterTypes.SORT_SOURCECOUNT_CURRENT:
      sortFunc =
        sortBySelector(m => m[type].sourceCountMetrics.current, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_SOURCECOUNT_BASELINE:
      sortFunc =
        sortBySelector(m => m[type].sourceCountMetrics.baseline, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_SOURCECOUNT_DIFF:
      sortFunc =
        sortBySelector(m => m[type].sourceCountMetrics.diff, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_SOURCEATTRIBUTECOUNT_CURRENT:
      sortFunc =
        sortBySelector(m =>
          m[type].sourceAttributeCountMetrics.current, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_SOURCEATTRIBUTECOUNT_BASELINE:
      sortFunc =
        sortBySelector(m =>
          m[type].sourceAttributeCountMetrics.baseline, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_SOURCEATTRIBUTECOUNT_DIFF:
      sortFunc =
      sortBySelector(m => m[type].sourceAttributeCountMetrics.diff, sorter.ascending);
      break;
    case metricsSorterTypes.SORT_IMAGE:
    default:
      sortFunc = sortByImage(sorter.ascending);
      // This is the default value
      break;
  }

  return metrics.sort(sortFunc);
};

export const getRollingAverage = (data, sensitivity) => {
  if (!data && data.length === 0) {
    return [];
  }

  let weight;
  if (sensitivity > 1) {
    weight = Math.min(100, sensitivity) / 100;
  } else if (sensitivity < 0) {
    weight = 0.01;
  } else {
    weight = sensitivity;
  }

  let avg = data[0];

  if (data.length > 1) {
    avg = (avg + data[1]) / 2;
  }

  const avgPoints = [avg];

  for (let i = 1; i < data.length; i++) {
    if (data[i]) {
      avg = data[i] * weight + avg * (1 - weight);
    }

    avgPoints.push(avg);
  }

  return avgPoints;
};

/**
 * Creates map where keys are a union of both given objects' keys and the values are
 * the difference of respective prop values.
 * @param {object} minuObj - The object whose property values will be used as minuends
 * @param {object} subtraObj - The object whose property values will be used as
 * subtrahends
 */
export const getDiffs = (minuObj, subtraObj) => {
  return union(keys(minuObj), keys(subtraObj)).reduce(
    (result, key) => {
      const leftOperand = minuObj[key] ? minuObj[key] : 0;
      const rightOperand = subtraObj[key] ? subtraObj[key] : 0;
      const diff = leftOperand - rightOperand;
      result[key] = diff;
      return result;
    }, {}
  );
};

export const getMetricExtremes = (metrics, propName) => {
  let max = -101;
  let min = Infinity;

  metrics.forEach(m => {
    const value = parseInt(m[propName]);

    if (isNaN(value)) {
      return;
    }

    max = max > value ? max : value;
    min = min < value ? min : value;
  });

  max = max === -101 ? 100 : max;

  min = min === Infinity ? -100 : min;
  min = min > 0 ? 0 : min;

  return {
    max,
    min
  };
};


// WEBPACK FOOTER //
// src/helpers/metrics.js